import React, { useState, useMemo } from 'react';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { useTasks } from '../../hooks/useTasks';
import TaskView from '../tasks/TaskView';
import TaskForm from '../tasks/TaskForm';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface ProjectCalendarViewProps {
  project: Project;
}

const ProjectCalendarView: React.FC<ProjectCalendarViewProps> = ({ project }) => {
  const { tasks, loading, updateTask, createTask } = useTasks();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [creatingTask, setCreatingTask] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Projekt feladatainak szűrése
  const projectTasks = useMemo(() => {
    return tasks.filter(task => task.projectId === project.id);
  }, [tasks, project.id]);

  // Hónap első és utolsó napja
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  // Hónap neve
  const monthName = currentDate.toLocaleDateString('hu-HU', { month: 'long', year: 'numeric' });

  // Előző/következő hónap
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Naptár napok generálása
  const calendarDays = useMemo(() => {
    const days: Date[] = [];
    const startDay = firstDayOfMonth.getDay();
    const adjustedStartDay = startDay === 0 ? 6 : startDay - 1; // Hétfő = 0

    // Előző hónap napjai
    for (let i = adjustedStartDay - 1; i >= 0; i--) {
      const date = new Date(firstDayOfMonth);
      date.setDate(date.getDate() - (i + 1));
      days.push(date);
    }

    // Aktuális hónap napjai
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), i));
    }

    // Következő hónap napjai (42 napig = 6 hét)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(lastDayOfMonth);
      date.setDate(date.getDate() + i);
      days.push(date);
    }

    return days;
  }, [currentDate, firstDayOfMonth, lastDayOfMonth]);

  // Task-ok nap szerint csoportosítva
  const tasksByDate = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    
    projectTasks.forEach(task => {
      if (task.dueDate) {
        const dateKey = new Date(task.dueDate).toDateString();
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(task);
      }
    });

    return grouped;
  }, [projectTasks]);

  // Státusz szín
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      todo: 'bg-gray-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  // Nap ellenőrzések
  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Betöltés...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Calendar Header */}
      <div className="mb-6 flex items-center justify-between bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
              <CalendarIcon className="h-6 w-6 text-white" />
            </div>
            <span className="bg-gradient-to-r from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 bg-clip-text text-transparent">
              {monthName}
            </span>
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 ml-14">
            {projectTasks.length} feladat összesen
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setCreatingTask(true);
              setSelectedDate(null);
            }}
            className="group relative px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-sm font-semibold flex items-center gap-2 overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            <PlusIcon className="h-5 w-5 relative z-10" />
            <span className="relative z-10">Új feladat</span>
          </button>
          
          <div className="w-px h-8 bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-600 to-transparent" />
          
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-900/60 backdrop-blur-md rounded-xl p-1.5 border border-gray-200/50 dark:border-gray-700/50">
            <button
              onClick={goToPreviousMonth}
              className="p-2 rounded-lg hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-200 group"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors" />
            </button>
            
            <button
              onClick={goToToday}
              className="px-4 py-2 rounded-lg bg-white/80 dark:bg-gray-800/80 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-all duration-200 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 border border-transparent hover:border-purple-200 dark:hover:border-purple-800"
            >
              Ma
            </button>
            
            <button
              onClick={goToNextMonth}
              className="p-2 rounded-lg hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-200 group"
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b-2 border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
          {['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'].map(day => (
            <div
              key={day}
              className="py-4 text-center text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 auto-rows-fr" style={{ minHeight: '600px' }}>
          {calendarDays.map((date, index) => {
            const dateKey = date.toDateString();
            const dayTasks = tasksByDate[dateKey] || [];
            const isTodayDate = isToday(date);
            const isCurrentMonthDate = isCurrentMonth(date);

            return (
              <div
                key={index}
                onDoubleClick={() => {
                  if (isCurrentMonthDate) {
                    setSelectedDate(date);
                    setCreatingTask(true);
                  }
                }}
                className={`border-r border-b border-gray-200/50 dark:border-gray-700/50 p-3 cursor-pointer hover:bg-gradient-to-br hover:from-purple-50/50 hover:to-transparent dark:hover:from-purple-900/10 dark:hover:to-transparent transition-all duration-200 ${
                  !isCurrentMonthDate ? 'bg-gray-50/50 dark:bg-gray-900/50' : ''
                } ${isTodayDate ? 'bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/30 dark:to-purple-800/20 ring-2 ring-purple-500/20 dark:ring-purple-400/20' : ''}`}
                title={isCurrentMonthDate ? 'Dupla kattintás új feladat létrehozásához' : ''}
              >
                {/* Date Number */}
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm font-semibold ${
                      isTodayDate
                        ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white w-8 h-8 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/50'
                        : isCurrentMonthDate
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-400 dark:text-gray-600'
                    }`}
                  >
                    {date.getDate()}
                  </span>
                  
                  {dayTasks.length > 0 && (
                    <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 px-2 py-0.5 rounded-full">
                      {dayTasks.length}
                    </span>
                  )}
                </div>

                {/* Tasks */}
                <div className="space-y-1">
                  {dayTasks.slice(0, 3).map(task => (
                    <div
                      key={task.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTask(task);
                      }}
                      className={`text-xs p-2 rounded-lg cursor-pointer hover:scale-105 hover:shadow-md transition-all duration-200 backdrop-blur-sm ${
                        task.status === 'completed'
                          ? 'bg-green-100/80 dark:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : task.status === 'in_progress'
                          ? 'bg-blue-100/80 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                          : 'bg-gray-100/80 dark:bg-gray-700/60 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <div className={`w-1.5 h-1.5 rounded-full ${getStatusColor(task.status)}`} />
                        <span className="truncate font-medium">{task.title}</span>
                      </div>
                    </div>
                  ))}
                  
                  {dayTasks.length > 3 && (
                    <div className="text-xs font-semibold text-purple-600 dark:text-purple-400 text-center py-1.5 bg-purple-50/50 dark:bg-purple-900/20 rounded-lg border border-purple-200/50 dark:border-purple-800/50">
                      +{dayTasks.length - 3} több
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && !editingTask && !creatingTask && (
        <TaskView
          task={selectedTask}
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          onEdit={(task) => setEditingTask(task)}
        />
      )}

      {/* Task Edit Form */}
      {editingTask && !creatingTask && (
        <TaskForm
          initialData={editingTask}
          isOpen={!!editingTask}
          onClose={() => {
            setEditingTask(null);
            setSelectedTask(null);
          }}
          onSubmit={async (taskData) => {
            try {
              await updateTask(editingTask.id, taskData);
              setEditingTask(null);
              setSelectedTask(null);
            } catch (error) {
              console.error('Error updating task:', error);
            }
          }}
        />
      )}

      {/* Task Create Form */}
      {creatingTask && (
        <TaskForm
          preSelectedProjectId={project.id}
          isOpen={creatingTask}
          onClose={() => {
            setCreatingTask(false);
            setSelectedDate(null);
          }}
          onSubmit={async (taskData) => {
            try {
              await createTask({
                ...taskData,
                projectId: project.id,
                dueDate: selectedDate || taskData.dueDate
              });
              setCreatingTask(false);
              setSelectedDate(null);
            } catch (error) {
              console.error('Error creating task:', error);
            }
          }}
        />
      )}
    </div>
  );
};

export default ProjectCalendarView;
